import { isValidAnswerSetTypeForQuestion } from '../components/AnswerSets/answer-set-utils';

// Base schemas
// const basePageSchema = {
//   type: 'object',
//   required: ['guid', 'pageType'],
//   properties: {
//     guid: { type: 'string' },
//     pageType: { type: 'string' },
//   },
// };

// const baseElementSchema = {
//   type: 'object',
//   required: ['guid', 'elementType'],
//   properties: {
//     guid: { type: 'string' },
//     elementType: { type: 'string' },
//   },
// };

// Question schemas
const textQuestionSchema = {
  type: 'object',
  required: ['text', 'key', 'type'],
  properties: {
    text: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Question text is required',
    },
    key: {
      type: 'string',
      minLength: 1,
      errorMessage: 'A unique key is required',
    },
    type: { enum: ['long-text'] },
  },
};

/** Question types a survey document may contain. */
export const SURVEY_QUESTION_TYPES = [
  'likert',
  'unscored-likert',
  'multi-select',
  'single-select',
];

/**
 * Question types a live document may contain. Sentiment has its own schema
 * (min 2 options). Other choice types use the generic multiple-choice schema.
 */
export const LIVE_QUESTION_TYPES = [
  'sentiment',
  'single-select',
  'multi-select',
  'likert',
  'unscored-likert',
];

const multipleChoiceQuestionSchema = {
  type: 'object',
  required: ['text', 'key', 'type'],
  hasChoices: true,
  properties: {
    text: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Question text is required',
    },
    key: {
      type: 'string',
      minLength: 1,
      errorMessage: 'A unique key is required',
    },
    type: {
      enum: [...new Set([...SURVEY_QUESTION_TYPES, ...LIVE_QUESTION_TYPES])],
      errorMessage: 'Did not recognize question type ${0}', // eslint-disable-line no-template-curly-in-string
    },
    answerSetId: { type: 'string' },
    // NOTE: disabling for now because the "hasChoices" keyword covers basic answer set validation
    // answerSet: {
    //   type: 'object',
    //   required: ['type', 'choices'],
    //   properties: {
    //     type: { type: 'string' },
    //     choices: { type: 'object' },
    //   },
    // },
  },
  errorMessage: {
    hasChoices: 'At least one option is required',
  },
};

/**
 * A sentiment scale needs at least two options to express a range. There is
 * deliberately no maximum: attaching a larger answer set stays valid and is
 * surfaced as a warning by `collectWarnings` instead.
 */
const sentimentQuestionSchema = {
  type: 'object',
  required: ['text', 'key', 'type'],
  minChoices: 2,
  properties: {
    text: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Question text is required',
    },
    key: {
      type: 'string',
      minLength: 1,
      errorMessage: 'A unique key is required',
    },
    type: { enum: ['sentiment'] },
    answerSetId: { type: 'string' },
    decoration: { enum: ['thumbs', 'faces', 'colors'] },
    config: {
      type: 'object',
      properties: {
        decoration: { enum: ['thumbs', 'faces', 'colors'] },
      },
    },
  },
  errorMessage: {
    minChoices: 'A sentiment scale needs at least 2 options',
  },
};

// Element schema with if/then conditions. Parameterized by the question types
// the containing assessment allows, so a live document rejects survey question
// types and vice versa.
function buildElementSchema(questionTypes) {
  // Only `sentiment` has a dedicated schema. Other live types (e.g.
  // single-select) must stay on the generic multiple-choice branch.
  const choiceTypes = questionTypes.filter((t) => t !== 'sentiment');
  const allowsSentiment = questionTypes.includes('sentiment');

  const questionBranches = [
    {
      if: { properties: { type: { enum: ['long-text'] } } },
      then: textQuestionSchema,
    },
  ];

  if (choiceTypes.length > 0) {
    questionBranches.push({
      if: { properties: { type: { enum: choiceTypes } } },
      then: multipleChoiceQuestionSchema,
    });
  }

  if (allowsSentiment) {
    questionBranches.push({
      if: { properties: { type: { enum: ['sentiment'] } } },
      then: sentimentQuestionSchema,
    });
  }

  return {
    type: 'object',
    required: ['guid', 'elementType'],
    properties: {
      guid: { type: 'string' },
      elementType: { type: 'string' },
    },
    allOf: [
      {
        if: { properties: { elementType: { const: 'text' } } },
        then: {
          required: ['content'],
          properties: {
            content: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Content is required',
            },
          },
        },
      },
      {
        if: { properties: { elementType: { const: 'question' } } },
        then: {
          required: ['type'],
          properties: {
            type: {
              type: 'string',
              enum: [...questionTypes, 'long-text'],
              errorMessage: 'Question type ${0} is not allowed here', // eslint-disable-line no-template-curly-in-string
            },
          },
          allOf: questionBranches,
        },
      },
    ],
  };
}

// Page schema with if/then conditions
function buildPageSchema(questionTypes) {
  return {
    type: 'object',
    required: ['guid', 'pageType'],
    properties: {
      guid: { type: 'string' },
      pageType: { type: 'string' },
    },
    allOf: [
      {
        if: {
          properties: { pageType: { const: 'info' } },
        },
        then: {
          required: ['title'],
          properties: {
            title: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Title is required',
            },
          },
        },
      },
      {
        if: { properties: { pageType: { const: 'question-set' } } },
        then: {
          required: ['name', 'key', 'elements'],
          properties: {
            name: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Name is required',
            },
            key: {
              type: 'string',
              minLength: 1,
              errorMessage: 'A unique key is required',
            },
            elements: {
              type: 'array',
              items: buildElementSchema(questionTypes),
            },
          },
        },
      },
    ],
  };
}

// Main assessment schema
export const assessmentSchema = {
  type: 'object',
  required: ['pages'],
  properties: {
    meta: {
      type: 'object',
      required: ['type', 'name', 'description'],
      properties: {
        type: {
          type: 'string',
          enum: ['survey', 'live'],
          errorMessage: 'Unrecognized assessment type ${0}', // eslint-disable-line no-template-curly-in-string
        },
        name: {
          type: 'string',
          minLength: 1,
          errorMessage: 'Name is required',
        },
        description: {
          type: 'string',
          minLength: 1,
          errorMessage: 'Description is required',
        },
      },
    },
    pages: { type: 'array' },
  },
  allOf: [
    {
      if: {
        required: ['meta'],
        properties: { meta: { properties: { type: { const: 'live' } } } },
      },
      then: {
        properties: {
          pages: { type: 'array', items: buildPageSchema(LIVE_QUESTION_TYPES) },
        },
      },
      else: {
        properties: {
          pages: {
            type: 'array',
            items: buildPageSchema(SURVEY_QUESTION_TYPES),
          },
        },
      },
    },
  ],
};

// Custom keywords for Ajv
export const customKeywords = {
  hasChoices: {
    keyword: 'hasChoices',
    type: 'object',
    validate(schema, data) {
      // const { data, schema } = ctx;
      const { answerSet, type: questionType } = data;

      let choiceCount = 0;
      if (
        answerSet != null &&
        isValidAnswerSetTypeForQuestion(questionType, answerSet.type)
      ) {
        choiceCount = Object.keys(answerSet?.choices ?? {}).length;
      }

      return choiceCount > 0;

      // ctx.fail(_`${choiceCount} === 0`);

      // let choices = 0;
      // if (answerSet == null ||
      //   !isValidAnswerSetTypeForQuestion(questionType, answerSet.type)
      // ) {
      //   ctx.errors.push({ message: 'At least one option is required' });
      //   return false;
      // } else {
      //   const choices = answerSet.choices ?? {};
      //   if (Object.keys(choices).length === 0) {
      //     ctx.errors.push({ message: 'At least one option is required' });
      //     return false;
      //   }
      // }

      // return true;

      // if (ctx.data.answerSetId && ctx.data.answerSet) {
      //   const validTypes = validAnswerSetTypes[ctx.data.type] || [];
      //   const isValid = validTypes.includes(ctx.data.answerSet.type);
      //   if (!isValid) {
      //     ctx.errors.push({
      //       message: `Invalid answer set type for question type ${ctx.data.type}`,
      //     });
      //   }
      // }
    },
    metaSchema: {
      const: true,
    },
  },
  minChoices: {
    keyword: 'minChoices',
    type: 'object',
    validate(schema, data) {
      const { answerSet, type: questionType } = data;

      if (
        answerSet == null ||
        !isValidAnswerSetTypeForQuestion(questionType, answerSet.type)
      ) {
        return false;
      }

      return Object.keys(answerSet.choices ?? {}).length >= schema;
    },
    metaSchema: {
      type: 'integer',
      minimum: 1,
    },
  },
  // validateAnswerSetType: {
  //   keyword: 'validateAnswerSetType',
  //   type: 'object',
  //   validate: function validate(schema, data) {
  //     if (data.answerSetId && data.answerSet && data.type) {
  //       const validTypes = validAnswerSetTypes[data.type] || [];
  //       const isValid = validTypes.includes(data.answerSet.type);

  //       if (!isValid) {
  //         validate.errors = [
  //           {
  //             keyword: 'validateAnswerSetType',
  //             message: `Invalid answer set type for question type ${data.type}`,
  //             params: {
  //               questionType: data.type,
  //               answerSetType: data.answerSet.type,
  //             },
  //           },
  //         ];
  //       }
  //       return isValid;
  //     }
  //     return true;
  //   },
  // },
};
